<template>
  <div id="chat" class="flex flex-col grid-container discussion-chat-message group">
    <!-- eslint-disable  vue/no-v-html -->
    <div class="flex md:flex-col flex-shrink-0 text-grey-300 text-15 pr-25 ">
      <div class="order-0 text-app-color flex items-center rounded-6 py-3 px-10 md:ml-auto max-w-125"
           :class="isDarkMode ? 'bg-book' : 'bg-white shadow-user-badge border border-grey-500'"
      >
        <UserBadge
          :first-name="message.user.profile.firstName"
          :last-name="message.user.profile.lastName"
          :color="message.user.profile.color"
          color-height="h-18"
          max-width="max-w-95"
          class="cursor-default"
        />
      </div>
      <p class="order-2 md:order-1 text-12 text-grey-300 text-right cursor-default mt-5 flex-shrink-0 ml-10 md:ml-0" :title="titleTime">{{ getTime }}</p>
      <ButtonModule
        v-if="message.resolvedAt"
        prefix="icon-resolved text-15"
        text="Resolved"
        class="order-1 md:order-2 rounded-6 font-semibold justify-center py-7 px-15 text-12 anim-color md:mt-5 bg-green-900 text-white shadow-sharp ml-10 md:ml-auto"
      />
      <div class="order-3 w-1/2 border-r h-calc mt-15 hidden md:block" :class="isDarkMode ? 'border-sidebar' : 'border-grey-425'" />
    </div>

    <div class="mt-10 md:mt-0">
      <div
        class="text-app-color rounded-8 break-words mb-10"
        :class="{'border-3 border-blue-100 p-10': message.resolvedAt}"
        v-html="replaceText"
      />
      <div class="flex">
        <Reactions :message-id="message.id" :message-reactions="messageReactions" :book-id="discussion.bookId" />
        <DropdownModule
          v-if="isOwner"
          icon="icon-more-horizontal text-24"
          :width="150"
          top-position="30px"
          append-to-body
          class="anim-opacity md:opacity-0 group-hover:opacity-100"
          :class="{'opacity-100': showMenu}"
          remove-optional-top-gap
          :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
          is-auto-menu-position
          @click="showMenu = true"
          @close="showMenu = false"
        >
          <div>
            <div class="hover:bg-selection rounded-t-4 group-scope text-15">
              <ButtonModule
                text="Edit"
                :prefix="`icon-edit mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                class="p-10 w-full"
                @click="toggleEdit"
              />
            </div>
            <div class="hover:bg-selection group-scope text-15">
              <ButtonModule
                text="Delete"
                :prefix="`icon-delete mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                class="p-10 w-full"
                @click="deleteMessage"
              />
            </div>
          </div>
        </DropdownModule>
      </div>
      <div v-if="!lastMessage" class="inline-block h-50 border-r ml-50 self-start md:hidden mt-20" :class="isDarkMode ? 'border-sidebar' : 'border-grey-425'" />
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from 'vuex'
import { parseDate } from '@/plugins/filters'

import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Reactions from '@/components/discussions/Reactions'
import UserBadge from '@/components/shared/UserBadge'

export default defineComponent({
  components: {
    DropdownModule,
    ButtonModule,
    Reactions,
    UserBadge
  },

  props: {
    message: Object,
    discussion: Object,
    lastMessage: Boolean
  },

  emits: ['editMessage'],

  setup (props, { emit }) {
    const { state, getters, dispatch } = useStore()

    const showMenu = ref(false)

    const user = computed(() => state.profile.user)
    const isOwner = computed(() => props.message.user?.id === state.profile.user.id)

    const messageReactions = computed(() => state.discussions.messageReactions.filter(item => item.discussionMessageId === +props.message.id))

    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const isDarkMode = computed(() => getters.isDarkMode)

    const getTime = computed(() => {
      const minutes = (Date.now() - new Date(props.message.createdAt)) / 1000 / 60
      if (minutes < 1) {
        return 'now'
      } else if (minutes < 2) {
        return '1 minute ago'
      } else if (minutes < 60) {
        return `${parseInt(minutes)} minutes ago`
      } else if (minutes / 60 < 2) {
        return '1 hour ago'
      } else if (minutes / 60 < 24) {
        return `${parseInt(minutes / 60)} hours ago`
      } else if (minutes / 60 > 24 && minutes / 60 / 24 < 8) {
        if (parseInt(minutes / 60 / 24) === 1) return `${parseInt(minutes / 60 / 24)} day ago`
        return `${parseInt(minutes / 60 / 24)} days ago`
      }
      return parseDate(props.message.createdAt, 'DD-MM-YYYY')
    })

    const titleTime = computed(() => {
      return parseDate(props.message.createdAt, 'DD-MM-YYYY hh:mm')
    })

    const hasText = computed(() => {
      var span = document.createElement('span')
      span.innerHTML = props.message.text
      return span.textContent.trim() !== '' || span.innerText.trim() !== ''
    })

    const replaceText = computed(() => {
      return props.message.text.replace(/<p><\/p>/g, '<br>').replace(/(@)+/g, '')
    })

    function toggleEdit () {
      emit('editMessage', props.message)
    }

    function deleteMessage () {
      dispatch('deleteDiscussionMessage', {
        id: props.message.id,
        bookId: props.discussion.bookId
      })
    }

    return {
      // data
      showMenu,
      // computed
      user,
      isOwner,
      getTime,
      hasText,
      titleTime,
      isDarkMode,
      replaceText,
      isTwilightTheme,
      messageReactions,
      // function
      toggleEdit,
      deleteMessage
    }
  }
})
</script>
